import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Select,
  VStack,
  HStack,
  Box,
  Text,
  Icon,
  Badge,
  Divider,
  Alert,
  AlertIcon,
  useColorModeValue,
  Collapse,
  SimpleGrid,
  Checkbox,
} from '@chakra-ui/react';
import {
  FaMagic,
  FaSearch,
  FaChevronDown,
  FaChevronUp,
  FaPlus,
  FaTrash,
} from 'react-icons/fa';
import { createDeck, updateDeck, analyzeDeckList } from '../../utils/deckApi';

export default function CreateDeckModal({
  isOpen,
  onClose,
  onDeckCreated,
  onDeckUpdated,
  editingDeck = null,
  userData,
}) {
  const [deckList, setDeckList] = useState('');
  const [parsedCards, setParsedCards] = useState([]);
  const [creating, setCreating] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [showFormatInfo, setShowFormatInfo] = useState(false);
  const [parseError, setParseError] = useState(null);
  const [parseSuccess, setParseSuccess] = useState(null);
  const [deckAnalysis, setDeckAnalysis] = useState(null);
  const [formData, setFormData] = useState({
    nomeMazzo: '',
    description: '',
    formato: 'Altro',
    bracket: 'Bracket 3',
    colors: '',
    isPublic: true,
  });

  const cardBg = useColorModeValue('white', 'gray.800');

  // Popola il form quando si sta modificando un deck esistente
  useEffect(() => {
    if (editingDeck) {
      setFormData({
        nomeMazzo: editingDeck.nomeMazzo || editingDeck.name || '',
        description: editingDeck.description || '',
        formato: editingDeck.formato || 'Altro',
        bracket: editingDeck.bracket || 'Bracket 3',
        colors: editingDeck.colors || '',
        isPublic: editingDeck.isPublic === true, // Esplicito: true solo se è esplicitamente true
      });

      // Se il deck ha già delle carte, le carica
      if (editingDeck.deck && editingDeck.deck.length > 0) {
        setParsedCards(editingDeck.deck);
        // Ricostruisci la lista di testo
        const deckListText = editingDeck.deck
          .map((card) => {
            const quantity = card.quantity || 1;
            return quantity > 1
              ? `${quantity} ${card.nome || card.name}`
              : card.nome || card.name;
          })
          .join('\n');
        setDeckList(deckListText);
        setDeckAnalysis({
          totalCards: editingDeck.deck.reduce(
            (total, card) => total + (card.quantity || 1),
            0
          ),
          totalLands: editingDeck.deck
            .filter((card) =>
              (card.type_line || '').toLowerCase().includes('land')
            )
            .reduce((total, card) => total + (card.quantity || 1), 0),
          totalCreatures: editingDeck.deck
            .filter((card) =>
              (card.type_line || '').toLowerCase().includes('creature')
            )
            .reduce((total, card) => total + (card.quantity || 1), 0),
          totalSpells: editingDeck.deck
            .filter(
              (card) =>
                !(card.type_line || '').toLowerCase().includes('land') &&
                !(card.type_line || '').toLowerCase().includes('creature')
            )
            .reduce((total, card) => total + (card.quantity || 1), 0),
        });
      }
    }
  }, [editingDeck]);

  // Formati disponibili (deve corrispondere ai valori di Strapi)
  const formati = [
    'Commander Tradizionale (EDH)',
    'Duel Commander',
    'Pauper',
    'Standard',
    'Historic',
    'Legacy',
    'Altro',
  ];

  // Bracket tiers (deve corrispondere ai valori di Strapi)
  const brackets = [
    'Bracket 1',
    'Bracket 2',
    'Bracket 3',
    'Bracket 4',
    'Bracket 5',
  ];

  // Funzione per parsare la lista di carte (non più usata - sostituita da analisi Scryfall)
  // const parseDeckList = (deckListText) => { ... };

  // Funzione per analizzare il mazzo
  const analyzeDeck = async () => {
    if (!deckList.trim()) {
      setParseError('Inserisci una lista di carte');
      return;
    }

    setAnalyzing(true);
    setParseError(null);
    setParseSuccess(null);

    try {
      // Analizza il mazzo per ottenere dati completi da Scryfall
      const response = await analyzeDeckList(deckList);
      console.log('Risposta analisi completa:', response);
      console.log('Tipo di response:', typeof response);
      console.log('response.cards:', response.cards);
      console.log('response.analysis:', response.analysis);

      if (response && response.cards && Array.isArray(response.cards)) {
        // Processa le carte per estrarre correttamente le quantità
        const processedCards = response.cards.map((card) => {
          // Cerca la riga corrispondente nella lista originale per estrarre la quantità
          const deckLines = deckList
            .split('\n')
            .map((line) => line.trim())
            .filter((line) => line);

          // Cerca la riga che contiene il nome della carta
          // Gestisce anche varianti come "Go-Shintai of Ancient Wars" vs "Go-Shintai of Ancient Wars (NEO) 144"
          const matchingLine = deckLines.find((line) => {
            const cardName = card.nome || card.name;
            // Rimuovi parentesi e numeri per un matching più accurato
            const cleanLine = line.replace(/\(\w+\)\s*\d*$/, '').trim();
            const cleanCardName = cardName.replace(/\(\w+\)\s*\d*$/, '').trim();

            return cleanLine.includes(cleanCardName) || line.includes(cardName);
          });

          if (matchingLine) {
            // Estrai la quantità dal formato "3 Go-Shintai of Ancient Wars (NEO) 144"
            // Cerca un numero all'inizio della riga seguito da uno spazio
            const quantityMatch = matchingLine.match(/^(\d+)\s+/);
            const quantity = quantityMatch ? parseInt(quantityMatch[1], 10) : 1;

            console.log(
              `Carta: ${card.nome || card.name}, Riga originale: "${matchingLine}", Quantità estratta: ${quantity}`
            );

            return { ...card, quantity };
          }

          return { ...card, quantity: 1 };
        });

        setParsedCards(processedCards);
        setDeckAnalysis(response.analysis);

        // Aggiorna automaticamente i colori del mazzo
        if (response.analysis && response.analysis.colors) {
          setFormData((prev) => ({
            ...prev,
            colors: response.analysis.colors,
          }));
        }

        setParseSuccess(
          `Analizzate ${processedCards.length} carte con dati Scryfall completi`
        );
      } else {
        console.error('Struttura risposta non valida:', response);
        throw new Error(
          `Risposta non valida dal server: ${JSON.stringify(response)}`
        );
      }
    } catch (error) {
      console.error("Errore nell'analisi del mazzo:", error);
      setParseError(`Errore nell'analisi del mazzo: ${error.message}`);
    } finally {
      setAnalyzing(false);
    }
  };

  // Funzione per creare o aggiornare il mazzo
  const handleCreateDeck = async () => {
    if (!formData.nomeMazzo.trim()) {
      setParseError('Inserisci un nome per il mazzo');
      return;
    }

    if (!deckList.trim()) {
      setParseError('Inserisci una lista di carte');
      return;
    }

    if (parsedCards.length === 0) {
      setParseError('Devi prima analizzare la lista di carte');
      return;
    }

    setCreating(true);

    try {
      const deckData = {
        ...formData,
        deck: parsedCards, // Usa direttamente i dati completi di Scryfall
        cardCount: parsedCards.reduce(
          (total, card) => total + (card.quantity || 1),
          0
        ),
        // Associa esplicitamente il deck all'utente autenticato tramite username
        user: {
          username: userData?.username,
        },
      };

      let result;
      if (editingDeck) {
        // Aggiorna mazzo esistente
        const deckId = editingDeck.documentId || editingDeck.id;
        // Mantieni l'associazione esistente o usa quella corrente
        const updateData = {
          ...deckData,
          user: editingDeck.user || { username: userData?.username },
        };
        result = await updateDeck(deckId, updateData);
        onDeckUpdated(result);
      } else {
        // Crea nuovo mazzo
        result = await createDeck(deckData);
        onDeckCreated(result);
      }

      onClose();

      // Reset form solo se non si sta modificando
      if (!editingDeck) {
        setDeckList('');
        setParsedCards([]);
        setFormData({
          nomeMazzo: '',
          description: '',
          formato: 'Altro',
          bracket: 'Bracket 3',
          colors: '',
          isPublic: true,
        });
        setDeckAnalysis(null);
        setParseError(null);
        setParseSuccess(null);
      }
    } catch (error) {
      setParseError(
        error.message ||
          (editingDeck
            ? "Errore nell'aggiornamento del mazzo"
            : 'Errore nella creazione del mazzo')
      );
    } finally {
      setCreating(false);
    }
  };

  // Funzione per rimuovere una carta dalla lista
  const removeCard = (index) => {
    const newCards = [...parsedCards];
    newCards.splice(index, 1);
    setParsedCards(newCards);

    // Ricostruisci la lista di testo con le quantità corrette
    const newDeckList = newCards
      .map((card) => {
        const quantity = card.quantity || 1;
        return quantity > 1
          ? `${quantity} ${card.nome || card.name}`
          : card.nome || card.name;
      })
      .join('\n');
    setDeckList(newDeckList);
  };

  // Funzione per modificare la quantità di una carta
  const updateCardQuantity = (index, newQuantity) => {
    if (newQuantity < 1 || newQuantity > 4) return;

    const newCards = [...parsedCards];
    newCards[index].quantity = newQuantity;
    setParsedCards(newCards);

    // Ricostruisci la lista di testo con le quantità corrette
    const newDeckList = newCards
      .map((card) => {
        const quantity = card.quantity || 1;
        return quantity > 1
          ? `${quantity} ${card.nome || card.name}`
          : card.nome || card.name;
      })
      .join('\n');
    setDeckList(newDeckList);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="6xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent maxW="90vw">
        <ModalHeader>
          <HStack spacing={3}>
            <Icon as={FaMagic} color="purple.500" />
            <Text>
              {editingDeck ? '✏️ Modifica Mazzo' : '🏗️ Crea Nuovo Mazzo'}
            </Text>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          <VStack spacing={6} align="stretch">
            {/* Form principale */}
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
              <VStack spacing={4} align="stretch">
                <FormControl isRequired>
                  <FormLabel>Nome del Mazzo</FormLabel>
                  <Input
                    placeholder="Es: Mono Red Aggro"
                    value={formData.nomeMazzo}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        nomeMazzo: e.target.value,
                      }))
                    }
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Descrizione</FormLabel>
                  <Textarea
                    placeholder="Descrivi la strategia del mazzo, le combo principali..."
                    value={formData.description}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        description: e.target.value,
                      }))
                    }
                    rows={3}
                  />
                </FormControl>

                <HStack spacing={4}>
                  <FormControl>
                    <FormLabel>Formato</FormLabel>
                    <Select
                      placeholder="Seleziona formato"
                      value={formData.formato}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          formato: e.target.value,
                        }))
                      }
                    >
                      {formati.map((format) => (
                        <option key={format} value={format}>
                          {format}
                        </option>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl>
                    <FormLabel>Bracket</FormLabel>
                    <Select
                      placeholder="Seleziona tier"
                      value={formData.bracket}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          bracket: e.target.value,
                        }))
                      }
                    >
                      {brackets.map((bracket) => (
                        <option key={bracket} value={bracket}>
                          {bracket}
                        </option>
                      ))}
                    </Select>
                  </FormControl>
                </HStack>

                <FormControl>
                  <FormLabel>Colori del Mazzo</FormLabel>
                  <Input
                    placeholder="Es: {R}{G} per Gruul"
                    value={formData.colors}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        colors: e.target.value,
                      }))
                    }
                  />
                  <Text fontSize="sm" color="gray.500" mt={1}>
                    Usa i simboli dei colori: W (Bianco), U (Blu), B (Nero), R
                    (Rosso), G (Verde), C (Incolore)
                  </Text>
                </FormControl>

                <FormControl>
                  <Checkbox
                    isChecked={formData.isPublic}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        isPublic: e.target.checked,
                      }))
                    }
                    colorScheme="purple"
                  >
                    <Text fontSize="sm" color="gray.700">
                      🌐 Mazzo pubblico (visibile a tutti i soci)
                    </Text>
                  </Checkbox>
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    I mazzi pubblici sono visibili a tutti i soci, quelli
                    privati solo a te
                  </Text>
                  <Text
                    fontSize="xs"
                    color="blue.600"
                    mt={1}
                    fontWeight="medium"
                  >
                    💡 <strong>Suggerimento:</strong> Se il mazzo è per un
                    torneo, si consiglia di deselezionare questa opzione per
                    mantenere la strategia segreta
                  </Text>
                </FormControl>
              </VStack>

              {/* Lista carte e analisi */}
              <VStack spacing={4} align="stretch">
                <FormControl isRequired>
                  <FormLabel>Lista Carte</FormLabel>
                  <Textarea
                    placeholder="Inserisci la lista delle carte (una per riga, formato: '2 Lightning Bolt' o 'Lightning Bolt')"
                    value={deckList}
                    onChange={(e) => setDeckList(e.target.value)}
                    rows={8}
                    fontFamily="mono"
                  />
                </FormControl>

                <HStack spacing={3}>
                  <Button
                    colorScheme="blue"
                    leftIcon={<FaSearch />}
                    onClick={analyzeDeck}
                    isLoading={analyzing}
                    loadingText="Analizzando..."
                  >
                    Analizza Mazzo
                  </Button>

                  <Button
                    variant="outline"
                    onClick={() => setShowFormatInfo(!showFormatInfo)}
                    rightIcon={
                      showFormatInfo ? <FaChevronUp /> : <FaChevronDown />
                    }
                  >
                    Formato
                  </Button>
                </HStack>

                {/* Info formato */}
                <Collapse in={showFormatInfo}>
                  <Box
                    p={4}
                    bg="blue.50"
                    borderRadius="md"
                    border="1px solid"
                    borderColor="blue.200"
                  >
                    <Text fontSize="sm" color="blue.700">
                      <strong>Formato lista carte:</strong>
                      <br />
                      • &ldquo;4 Lightning Bolt&rdquo; - 4 copie di Lightning
                      Bolt
                      <br />
                      • &ldquo;Lightning Bolt&rdquo; - 1 copia di Lightning Bolt
                      (quantità opzionale)
                      <br />
                      • &ldquo;2 Counterspell&rdquo; - 2 copie di Counterspell
                      <br />
                      • Le righe vuote e i commenti (//) vengono ignorati
                      <br />• Clicca &ldquo;Analizza&rdquo; per ottenere i dati
                      completi da Scryfall
                    </Text>
                  </Box>
                </Collapse>

                {/* Messaggi di stato */}
                {parseError && (
                  <Alert status="error">
                    <AlertIcon />
                    <Text fontSize="sm" whiteSpace="pre-line">
                      {parseError}
                    </Text>
                  </Alert>
                )}

                {parseSuccess && (
                  <Alert status="success">
                    <AlertIcon />
                    <Text fontSize="sm">{parseSuccess}</Text>
                  </Alert>
                )}

                {/* Analisi del mazzo */}
                {deckAnalysis && (
                  <Box
                    p={4}
                    bg="green.50"
                    borderRadius="md"
                    border="1px solid"
                    borderColor="green.200"
                  >
                    <Text fontWeight="semibold" color="green.700" mb={2}>
                      📊 Analisi del Mazzo
                    </Text>
                    <SimpleGrid columns={2} spacing={2}>
                      <Text fontSize="sm">
                        Totale carte:{' '}
                        <strong>
                          {parsedCards.reduce(
                            (total, card) => total + (card.quantity || 1),
                            0
                          )}
                        </strong>
                      </Text>
                      <Text fontSize="sm">
                        Terre:{' '}
                        <strong>
                          {parsedCards
                            .filter(
                              (card) =>
                                card.type_line &&
                                card.type_line.includes('Land')
                            )
                            .reduce(
                              (total, card) => total + (card.quantity || 1),
                              0
                            )}
                        </strong>
                      </Text>
                      <Text fontSize="sm">
                        Creature:{' '}
                        <strong>
                          {parsedCards
                            .filter(
                              (card) =>
                                card.type_line &&
                                card.type_line.includes('Creature')
                            )
                            .reduce(
                              (total, card) => total + (card.quantity || 1),
                              0
                            )}
                        </strong>
                      </Text>
                      <Text fontSize="sm">
                        Magie:{' '}
                        <strong>
                          {parsedCards
                            .filter(
                              (card) =>
                                card.type_line &&
                                !card.type_line.includes('Land') &&
                                !card.type_line.includes('Creature')
                            )
                            .reduce(
                              (total, card) => total + (card.quantity || 1),
                              0
                            )}
                        </strong>
                      </Text>
                      <Text fontSize="sm">
                        CMC medio: <strong>{deckAnalysis.averageCMC}</strong>
                      </Text>
                      <Text fontSize="sm">
                        Colori: <strong>{deckAnalysis.colors}</strong>
                      </Text>
                    </SimpleGrid>
                  </Box>
                )}

                {/* Contatore totale carte in tempo reale */}
                {parsedCards.length > 0 && (
                  <Box
                    p={3}
                    bg="blue.50"
                    borderRadius="md"
                    border="1px solid"
                    borderColor="blue.200"
                  >
                    <Text fontSize="sm" color="blue.700">
                      <strong>📊 Contatore Carte:</strong>{' '}
                      {parsedCards.reduce(
                        (total, card) => total + (card.quantity || 1),
                        0
                      )}{' '}
                      totali / {parsedCards.length} uniche
                    </Text>
                  </Box>
                )}
              </VStack>
            </SimpleGrid>

            {/* Lista carte parsate */}
            {parsedCards.length > 0 && (
              <>
                <Divider />
                <Box>
                  <Text fontWeight="semibold" mb={3}>
                    📋 Carte nel Mazzo (
                    {parsedCards.reduce(
                      (total, card) => total + (card.quantity || 1),
                      0
                    )}{' '}
                    totali, {parsedCards.length} uniche)
                  </Text>
                  <VStack
                    spacing={2}
                    align="stretch"
                    maxH="300px"
                    overflowY="auto"
                  >
                    {parsedCards.map((card, index) => (
                      <HStack
                        key={`${card.nome || card.name}-${card.set || card.setCode || 'default'}-${card.scryfallId || card.id || index}`}
                        p={3}
                        bg={cardBg}
                        borderRadius="md"
                        boxShadow="sm"
                        justify="space-between"
                      >
                        <HStack spacing={3}>
                          <Badge colorScheme="blue" variant="outline">
                            {card.quantity || 1}x
                          </Badge>
                          <Text fontWeight="medium">
                            {card.nome || card.name}
                          </Text>
                          {card.set && (
                            <Badge colorScheme="purple" variant="outline">
                              {card.set.toUpperCase()}
                            </Badge>
                          )}
                          {card.rarity && (
                            <Badge colorScheme="orange" variant="outline">
                              {card.rarity}
                            </Badge>
                          )}
                          {card.cmc > 0 && (
                            <Badge colorScheme="green" variant="outline">
                              CMC: {card.cmc}
                            </Badge>
                          )}
                        </HStack>

                        <HStack spacing={2}>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() =>
                              updateCardQuantity(
                                index,
                                (card.quantity || 1) - 1
                              )
                            }
                            isDisabled={(card.quantity || 1) <= 1}
                          >
                            -
                          </Button>
                          <Text
                            fontWeight="bold"
                            minW="20px"
                            textAlign="center"
                          >
                            {card.quantity || 1}
                          </Text>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() =>
                              updateCardQuantity(
                                index,
                                (card.quantity || 1) + 1
                              )
                            }
                            isDisabled={(card.quantity || 1) >= 4}
                          >
                            +
                          </Button>
                          <Button
                            size="sm"
                            colorScheme="red"
                            variant="outline"
                            onClick={() => removeCard(index)}
                            leftIcon={<FaTrash />}
                          >
                            Rimuovi
                          </Button>
                        </HStack>
                      </HStack>
                    ))}
                  </VStack>
                </Box>
              </>
            )}
          </VStack>
        </ModalBody>

        <ModalFooter>
          <HStack spacing={3}>
            <Button variant="ghost" onClick={onClose}>
              Annulla
            </Button>
            {editingDeck && (
              <Button
                variant="outline"
                onClick={() => {
                  // Reset form ai valori originali del deck
                  if (editingDeck) {
                    setFormData({
                      nomeMazzo:
                        editingDeck.nomeMazzo || editingDeck.name || '',
                      description: editingDeck.description || '',
                      formato: editingDeck.formato || 'Altro',
                      bracket: editingDeck.bracket || 'Bracket 3',
                      colors: editingDeck.colors || '',
                    });
                    if (editingDeck.deck && editingDeck.deck.length > 0) {
                      setParsedCards(editingDeck.deck);
                      const deckListText = editingDeck.deck
                        .map((card) => {
                          const quantity = card.quantity || 1;
                          return quantity > 1
                            ? `${quantity} ${card.nome || card.name}`
                            : card.nome || card.name;
                        })
                        .join('\n');
                      setDeckList(deckListText);
                    }
                  }
                }}
              >
                Reset
              </Button>
            )}
            <Button
              colorScheme="purple"
              leftIcon={editingDeck ? <FaSearch /> : <FaPlus />}
              onClick={handleCreateDeck}
              isLoading={creating}
              loadingText={editingDeck ? 'Aggiornando...' : 'Creando...'}
              isDisabled={
                !formData.nomeMazzo.trim() || parsedCards.length === 0
              }
            >
              {editingDeck ? 'Aggiorna Mazzo' : 'Crea Mazzo'}
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
